/**
 * 방문자 접근 관리 섹션 컴포넌트
 * 방문자 초대 기능, 출입 권한 설정, 초대 설정을 관리하는 컴포넌트입니다.
 */

import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import { AcGroupContractManageModel } from "src/api/access/ac-group-types";
import { ContractOperationProductManage } from "src/api/contract/contract-types";
import { VisitorApproverMemberType } from "src/api/product/product-types";
import { BaseButton, BaseCheckbox, BaseInput, BaseRadio, BaseToggle, BaseTooltip } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import Link from "src/components/PartnerLink";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { globalPartnerState } from "src/recoil/partners/atom";
import { PaPartner } from "src/types/partner";
import {
  onlyNumber,
  VISITOR_INVITE_APPROVER_LABEL,
  VISITOR_OPTION_LABEL_CLASS,
  visitorApprovalSummaryLabel,
  VisitorApprovalMode,
  visitorApproverChecksToType,
  visitorApproverTypeToChecks,
  visitorApproverTypeToMode,
} from "src/utils";

/**
 * 방문자 접근 관리 섹션 컴포넌트의 props 인터페이스
 */
type Props = {
  productManage: ContractOperationProductManage; // 상품 관리 정보
  contractManage: AcGroupContractManageModel | undefined; // 계약 관리 정보
  isAuthority?: boolean; // 권한 여부
  onChangeVisitor: (data: { productManage?: ContractOperationProductManage; visitorDefaultAccessGroupYn?: boolean }) => Promise<void>; // 방문자 관련 변경 핸들러
  visitorUrl: string; // 방문자 초대 URL
};

/**
 * 방문자 접근 관리 섹션 컴포넌트
 * @param productManage 상품 관리 정보
 * @param contractManage 계약 관리 정보
 * @param onChangeVisitor 방문자 관련 변경 핸들러
 * @param isAuthority 권한 여부
 * @param visitorUrl 방문자 초대 URL
 */
const VisitorAccessSection = ({ productManage, contractManage, onChangeVisitor, isAuthority, visitorUrl }: Props) => {
  const [isEdit, setIsEdit] = useState(false);
  const partner = useRecoilValue(globalPartnerState);
  const {
    setValue,
    register,
    control,
    watch,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      isUseVisitorInvite: false,
      visitorDefaultAccessGroupYn: false,
      isVisitorUnlimited: false,
      visitorMaxNums: 0,
      isUseVisitorTenantManagerApproval: false,
      isUseVisitorSelfInvite: false,
      // 신규 상품 생성 시 기본값은 빈 값 (승인방식/승인자 미선택)
      visitorApproverMemberType: "" as VisitorApproverMemberType | "",
      // 화면 전용 필드 > 저장 시에는 visitorApproverMemberType 하나로 합쳐 전달한다
      visitorApprovalMode: "" as VisitorApprovalMode,
    },
  });

  useEffect(() => {
    if (productManage && contractManage) {
      setValue("isUseVisitorInvite", productManage.isUseVisitorInvite);
      setValue("visitorDefaultAccessGroupYn", contractManage.visitorDefaultAccessGroupYn);
      setValue("isVisitorUnlimited", productManage.isVisitorUnlimited);
      setValue("visitorMaxNums", productManage.visitorMaxNums);
      setValue("isUseVisitorTenantManagerApproval", productManage.isUseVisitorTenantManagerApproval ?? false);
      setValue("isUseVisitorSelfInvite", productManage.isUseVisitorSelfInvite ?? false);
      setValue("visitorApproverMemberType", productManage.visitorApproverMemberType ?? "");
      setValue("visitorApprovalMode", visitorApproverTypeToMode(productManage.visitorApproverMemberType));
    }
  }, [productManage, contractManage, setValue]);

  const formValidation = useCallback(() => {
    const requiredMessage = "필수 입력 항목입니다";
    register("visitorMaxNums", {
      required: { value: true, message: requiredMessage },
    });
  }, [register]);

  const onSubmit = useCallback(
    async (data: any) => {
      console.log("data", data);

      const calendarType = productManage.calendarType !== "" ? productManage.calendarType : "NONE";
      const updatedProductManage = {
        ...productManage,
        isUseVisitorInvite: data.isUseVisitorInvite,
        isVisitorUnlimited: data.isVisitorUnlimited,
        visitorMaxNums: data.visitorMaxNums,
        isUseVisitorTenantManagerApproval: data.isUseVisitorTenantManagerApproval,
        isUseVisitorSelfInvite: data.isUseVisitorSelfInvite,
        visitorApproverMemberType: data.visitorApproverMemberType,
        calendarType,
      };

      // 하나의 핸들러로 모든 방문자 관련 변경사항 전달
      await onChangeVisitor({
        productManage: updatedProductManage,
        visitorDefaultAccessGroupYn: data.visitorDefaultAccessGroupYn,
      });

      setIsEdit(false);
    },
    [onChangeVisitor, productManage],
  );

  // 방문자 출입 권한 선택 기능 여부 > false 일 경우 비활성화
  const isVisitorAcSelect =
    partner?.features?.some((feature: PaPartner) => feature.name === "CTRLROOM_VISITOR_ACCESS_GROUP_SELECT" && feature.active) || false;

  // 방문자 셀프 등록 기능 여부 > false 일 경우 비활성화
  const isVisitorSelfRegist =
    partner?.features?.some((feature: PaPartner) => feature.name === "FEATURE_VISITOR_SELF_REGIST" && feature.active) || false;

  // 방문자 초대 승인 기능(이용자 마스터 승인) feature 여부 > false 일 경우 비활성화
  const isVisitorInviteApprovalFeature =
    partner?.features?.some((feature: PaPartner) => feature.name === "FEATURE_VISITOR_TENANT_MANAGER_APPROVAL" && feature.active) || false;

  // 방문자 초대 기능 사용 여부 > 하위 설정(회색 박스) 전체의 활성화 조건
  const isVisitorInviteOn = watch("isUseVisitorInvite") === true;
  // 방문자 초대 승인 기능 설정 가능 상태 (feature on + 방문자 초대 기능 사용)
  const isInviteApprovalEditable = isVisitorInviteApprovalFeature && isVisitorInviteOn;
  // 방문자 셀프 등록 승인방식이 필수인 상태 (셀프 등록 사용 시)
  // 방문자 초대 기능 사용 여부와 무관하게 검증한다 > 어떤 경우에도 승인방식이 빈 값으로 저장되면 안됨
  const isApproverRequired = isVisitorSelfRegist && watch("isUseVisitorSelfInvite");
  // 승인방식/승인자 설정 조작 가능 상태 (방문자 초대 기능 미사용 시 회색 박스 전체 비활성)
  const isApproverEditable = isApproverRequired && isVisitorInviteOn;
  // 승인자 설정(이용자/이용자 마스터)은 수동승인일 때만 조작 가능
  const isManualApproval = watch("visitorApprovalMode") === "MANUAL";
  // 미선택 시 저장 차단/에러 노출은 react-hook-form 검증(rules.validate)에서 처리한다

  return (
    <section className="contents-container__1200">
      <form onSubmit={handleSubmit(onSubmit)}>
        <BaseSectionTitle
          title={"방문자 관리"}
          className="no-max-width"
          rightArea={
            isAuthority ? (
              <>
                {!isEdit ? (
                  <BaseButton title="수정" className="size-small" onClick={() => setIsEdit(true)} />
                ) : (
                  <div className="flex-start-start gap10">
                    <BaseButton title="취소" className="size-small color-white" onClick={() => setIsEdit(false)} />
                    <BaseButton title="저장" type="submit" className="size-small" onClick={formValidation} />
                  </div>
                )}
              </>
            ) : (
              <></>
            )
          }
        />
        <div className="">
          {/* 방문자 초대 기능 설정 섹션 */}
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>방문자 초대 기능</p>
            </div>
            <div className="contents-container__grid-contents">
              {isEdit ? (
                <div className="flex-row flex-center-start">
                  <Controller
                    control={control}
                    name="isUseVisitorInvite"
                    render={({ field: { value, name } }) => (
                      <BaseToggle
                        id="isUseVisitorInvite"
                        name={name}
                        checked={value === true}
                        onChange={(checked: boolean) => setValue("isUseVisitorInvite", checked, { shouldValidate: true })}
                      />
                    )}
                  />
                </div>
              ) : (
                <p>{productManage?.isUseVisitorInvite ? "ON" : "OFF"}</p>
              )}
            </div>
          </section>
          {/* 방문자 초대 기능 하위 설정 > 방문자 초대 기능 사용 시에만 설정 가능 */}
          <div className="contents-container__grid-box">
            {/* 최대 방문자 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index flex-start-start">
                <p>최대 방문자</p>
                <BaseTooltip touchIcon={"QUESTION"}>
                  <ul className="base-list">
                    <li>
                      계약 기간 동안 초대할 수 있는 방문자의 총 인원수를 제한할 수 있습니다. 초대를 취소(삭제)하면 해당 인원만큼 다시 초대할 수
                      있습니다.
                    </li>
                  </ul>
                </BaseTooltip>
              </div>
              <div className="contents-container__grid-contents">
                {isEdit ? (
                  <div className="flex-row flex-center-start">
                    <Controller
                      control={control}
                      name="isVisitorUnlimited"
                      render={({ field: { value, name } }) => (
                        <>
                          {[
                            { value: true, label: "무제한", labelClassName: VISITOR_OPTION_LABEL_CLASS },
                            // '제한'은 인풋이 붙는 선택지이므로 텍스트 영역 폭 고정 예외
                            { value: false, label: "제한", labelClassName: "" },
                          ].map((data, index) => (
                            <BaseRadio
                              key={index}
                              id={data.label}
                              name={name}
                              label={data.label}
                              labelClassName={data.labelClassName}
                              onChange={(e) => {
                                if (e) {
                                  setValue("isVisitorUnlimited", data.value, { shouldValidate: true });
                                  if (data.value === true) {
                                    setValue("visitorMaxNums", 0, { shouldValidate: false });
                                    clearErrors("visitorMaxNums");
                                  }
                                }
                              }}
                              checked={value === data.value}
                              disabled={!isVisitorInviteOn}
                              className="mr10"
                            />
                          ))}
                        </>
                      )}
                    />
                    <Controller
                      control={control}
                      name="visitorMaxNums"
                      rules={{
                        required: {
                          value: !watch("isVisitorUnlimited"),
                          message: "필수 입력 항목입니다",
                        },
                        validate: (value) => {
                          // 무제한이 선택되어 있으면 validation 건너뛰기
                          // 방문자 초대 기능 미사용이어도 '제한'이 선택되어 있으면 필수 입력
                          if (watch("isVisitorUnlimited")) {
                            return true;
                          }
                          const numValue = Number(value);
                          if (isNaN(numValue) || numValue <= 0) {
                            return "필수 입력 항목입니다";
                          }
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value, name } }) => (
                        <BaseInput
                          maxLength={8}
                          name={name}
                          className="minmax220"
                          placeholder="최대 인원수를 입력해주세요."
                          onChange={(inputValue: string) => {
                            const numericValue = onlyNumber(inputValue);
                            onChange(numericValue);
                          }}
                          value={value}
                          disabled={watch("isVisitorUnlimited") || !isVisitorInviteOn}
                        />
                      )}
                    />
                  </div>
                ) : (
                  <p>
                    {productManage?.isVisitorUnlimited ? "무제한" : `제한 (${productManage?.visitorMaxNums ? productManage?.visitorMaxNums : 0}명)`}
                  </p>
                )}
                {/* 유효성 메시지는 인풋 하단에 영역을 차지하며 노출한다 */}
                {isEdit && errors?.visitorMaxNums && (
                  <p className="validation-text pl10" style={{ paddingTop: 0 }}>
                    {String(errors?.visitorMaxNums?.message || "")}
                  </p>
                )}
              </div>
            </section>
            {/* 방문자 초대 승인 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index flex-start-start">
                <p>방문자 초대 승인</p>
                <BaseTooltip touchIcon={"QUESTION"}>
                  <ul className="base-list">
                    <li>
                      사용 설정 시, 이용자가 Mobile App의 [방문자 초대] 또는 Ctrl.room의 [방문자] 메뉴로 초대한 방문자는 즉시 확정되지 않고 이용자
                      마스터 승인 후 초대가 확정됩니다.
                    </li>
                  </ul>
                </BaseTooltip>
              </div>
              <div className="contents-container__grid-contents">
                {isEdit ? (
                  <div className="flex-row flex-center-start">
                    <Controller
                      control={control}
                      name="isUseVisitorTenantManagerApproval"
                      render={({ field: { value, name } }) => (
                        <>
                          {[
                            { value: true, label: "사용" },
                            { value: false, label: "사용안함" },
                          ].map((data, index) => (
                            <BaseRadio
                              key={index}
                              id={`isUseVisitorTenantManagerApproval-${data.label}`}
                              name={name}
                              label={data.label}
                              labelClassName={VISITOR_OPTION_LABEL_CLASS}
                              onChange={(e) => {
                                if (e) {
                                  setValue("isUseVisitorTenantManagerApproval", data.value, { shouldValidate: true });
                                }
                              }}
                              checked={value === data.value}
                              disabled={!isInviteApprovalEditable}
                              className="mr10"
                            />
                          ))}
                        </>
                      )}
                    />
                  </div>
                ) : (
                  <p>{productManage?.isUseVisitorTenantManagerApproval ? "사용" : "사용안함"}</p>
                )}
              </div>
            </section>
            {/* 초대 승인자 > 방문자 초대 승인의 하위 설정, 이용자 마스터로 고정 (선택 불가) */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>ㄴ 초대 승인자</p>
              </div>
              <div className="contents-container__grid-contents">
                {isEdit ? (
                  <div className="flex-row flex-center-start">
                    {[
                      { key: "isInviter", label: "이용자", checked: false },
                      { key: "isAdmin", label: "이용자 마스터", checked: true },
                    ].map((data) => (
                      <BaseCheckbox
                        key={data.key}
                        id={`isUseVisitorTenantManagerApproval-approver-${data.label}`}
                        name="visitorInviteApprover"
                        label={data.label}
                        labelClassName={VISITOR_OPTION_LABEL_CLASS}
                        checked={data.checked}
                        disabled
                        className="mr10"
                        onChange={() => {}}
                      />
                    ))}
                  </div>
                ) : (
                  <p>{VISITOR_INVITE_APPROVER_LABEL}</p>
                )}
              </div>
            </section>
            {/* 방문자 셀프 등록 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index flex-start-start">
                <p>방문자 셀프 등록</p>
                <BaseTooltip touchIcon={"QUESTION"}>
                  <ul className="base-list">
                    <li>
                      방문자가 방문자 셀프 등록 서비스 링크로 접속해 방문 정보를 직접 입력하여 등록합니다. 등록된 방문자는 승인 유형에 따라 자동
                      승인되거나 셀프 등록 승인자의 승인 후 방문이 확정됩니다.
                    </li>
                  </ul>
                </BaseTooltip>
              </div>
              <div className="contents-container__grid-contents">
                {isEdit ? (
                  <div className="flex-row flex-center-start">
                    <Controller
                      control={control}
                      name="isUseVisitorSelfInvite"
                      render={({ field: { value, name } }) => (
                        <>
                          {[
                            { value: true, label: "사용" },
                            { value: false, label: "사용안함" },
                          ].map((data, index) => (
                            <BaseRadio
                              key={index}
                              id={`isUseVisitorSelfInvite-${data.label}`}
                              name={name}
                              label={data.label}
                              labelClassName={VISITOR_OPTION_LABEL_CLASS}
                              onChange={(e) => {
                                if (e) {
                                  setValue("isUseVisitorSelfInvite", data.value, { shouldValidate: true });
                                  // 셀프 등록을 사용으로 전환하면 승인방식 기본값은 자동승인
                                  if (data.value === true && !watch("visitorApprovalMode")) {
                                    setValue("visitorApprovalMode", "AUTO", { shouldValidate: true });
                                    setValue("visitorApproverMemberType", "MEMBER_AUTO", { shouldValidate: true });
                                  }
                                }
                              }}
                              checked={value === data.value}
                              disabled={!isVisitorSelfRegist || !isVisitorInviteOn}
                              className="mr10"
                            />
                          ))}
                        </>
                      )}
                    />
                  </div>
                ) : (
                  <p>{productManage?.isUseVisitorSelfInvite ? "사용" : "사용안함"}</p>
                )}
              </div>
            </section>
            {/* 승인방식 > 방문자 셀프 등록의 하위 설정, 수동승인 선택 시 승인자 설정을 같은 행에 노출 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>ㄴ 승인방식</p>
              </div>
              <div className="contents-container__grid-contents">
                {isEdit ? (
                  <div className="flex-row flex-center-start">
                    <Controller
                      control={control}
                      name="visitorApprovalMode"
                      rules={{
                        validate: (value) => {
                          // 셀프 등록 사용 시에만 승인방식 필수
                          // 방문자 초대 기능 미사용이어도 검증한다 (라디오는 비활성이지만 빈 값 저장은 불가)
                          if (isApproverRequired) {
                            return value ? true : "필수 입력 항목입니다";
                          }
                          return true;
                        },
                      }}
                      render={({ field: { value, name } }) => (
                        <>
                          {[
                            { value: "AUTO" as const, label: "자동승인", labelClassName: VISITOR_OPTION_LABEL_CLASS },
                            // '수동승인'은 승인자 설정이 붙는 선택지이므로 텍스트 영역 폭 고정 예외
                            { value: "MANUAL" as const, label: "수동승인", labelClassName: "" },
                          ].map((data) => (
                            <BaseRadio
                              key={data.value}
                              id={`visitorApprovalMode-${data.label}`}
                              name={name}
                              label={data.label}
                              labelClassName={data.labelClassName}
                              onChange={(e) => {
                                if (!e) return;
                                setValue("visitorApprovalMode", data.value, { shouldValidate: true });
                                // 자동승인은 승인자 설정이 없으므로 유형을 MEMBER_AUTO 로 고정하고,
                                // 수동승인으로 전환하면 승인자를 다시 선택하도록 빈 값으로 되돌린다
                                setValue("visitorApproverMemberType", data.value === "AUTO" ? "MEMBER_AUTO" : "", {
                                  shouldValidate: true,
                                });
                              }}
                              checked={value === data.value}
                              disabled={!isApproverEditable}
                              className="mr10"
                            />
                          ))}
                        </>
                      )}
                    />
                    {/* 승인자 설정 > 수동승인의 하위 설정이므로 수동승인일 때만 조작 가능 */}
                    <Controller
                      control={control}
                      name="visitorApproverMemberType"
                      rules={{
                        validate: (value) => {
                          // 수동승인일 때만 이용자/이용자 마스터 최소 1개 필수
                          if (isApproverRequired && isManualApproval) {
                            return value ? true : "필수 입력 항목입니다";
                          }
                          return true;
                        },
                      }}
                      render={({ field: { value, name } }) => {
                        const checks = visitorApproverTypeToChecks(value);
                        const disabled = !isApproverEditable || !isManualApproval;
                        return (
                          <div className="flex-row flex-center-start">
                            <span className="font14">(</span>
                            {[
                              { key: "isInviter" as const, label: "이용자", checked: checks.isInviter },
                              { key: "isAdmin" as const, label: "이용자 마스터", checked: checks.isAdmin },
                            ].map((data, index) => (
                              <BaseCheckbox
                                key={data.key}
                                id={`visitorApproverMemberType-${data.label}`}
                                name={name}
                                label={data.label}
                                checked={data.checked}
                                disabled={disabled}
                                className={index === 0 ? "ml4" : "ml10"}
                                onChange={(checked) => {
                                  const next = visitorApproverChecksToType({ ...checks, [data.key]: checked });
                                  setValue("visitorApproverMemberType", next, { shouldValidate: true });
                                }}
                              />
                            ))}
                            <span className="font14 ml4">)</span>
                          </div>
                        );
                      }}
                    />
                  </div>
                ) : (
                  <p>{visitorApprovalSummaryLabel(productManage?.visitorApproverMemberType)}</p>
                )}
                {/* 유효성 메시지는 '승인방식'의 첫 선택지(자동승인) 하단 좌측에 영역을 차지하며 노출한다 */}
                {isEdit && (errors?.visitorApprovalMode || errors?.visitorApproverMemberType) && (
                  <p className="validation-text pl10" style={{ paddingTop: 0 }}>
                    {String(errors?.visitorApprovalMode?.message || errors?.visitorApproverMemberType?.message || "")}
                  </p>
                )}
              </div>
            </section>
          </div>
          {/* 방문자 출입 권한 / 방문자 초대 바로가기 섹션 */}
          <div className="flex-center-center gap10">
            {/* 방문자 출입 권한 설정 섹션 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>방문자 출입 권한</p>
              </div>
              <div className="contents-container__grid-contents">
                {isEdit ? (
                  <>
                    <div className="flex-row flex-center-start">
                      <Controller
                        control={control}
                        name="visitorDefaultAccessGroupYn"
                        render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                          <>
                            <div className="flex-row flex-center-start mr10">
                              <BaseRadio
                                id="기본 권한"
                                name={name}
                                label="기본 권한"
                                onChange={(e) => {
                                  if (e) {
                                    setValue("visitorDefaultAccessGroupYn", true, { shouldValidate: true });
                                  }
                                }}
                                checked={value === true}
                                disabled={!isVisitorAcSelect}
                              />
                              <BaseTooltip contents="방문자의 출입권한은 계약을 따릅니다." touchIcon="QUESTION" />
                            </div>
                            <div className="flex-row flex-center-start">
                              <BaseRadio
                                id="커스텀 권한"
                                name={name}
                                label="커스텀 권한"
                                onChange={(e) => {
                                  if (e) {
                                    setValue("visitorDefaultAccessGroupYn", false, { shouldValidate: true });
                                  }
                                }}
                                checked={value === false}
                                disabled={!isVisitorAcSelect}
                              />
                              <BaseTooltip
                                contents="방문자를 초대할 때마다 출입권한을 다르게 설정할 수 있습니다."
                                className="mr10"
                                touchIcon="QUESTION"
                              />
                            </div>
                          </>
                        )}
                      />
                    </div>
                    <ul className="base-list">
                      <li className="font13">이미 초대한 방문자의 권한에도 적용됩니다.</li>
                    </ul>
                  </>
                ) : (
                  <div className="flex-row flex-center-start">
                    <div className="flex-row flex-center-start mr10">
                      <p>{contractManage?.visitorDefaultAccessGroupYn ? "기본 권한" : "커스텀 권한"}</p>
                      <BaseTooltip
                        contents={
                          contractManage?.visitorDefaultAccessGroupYn
                            ? "방문자의 출입권한은 계약을 따릅니다."
                            : "방문자를 초대할 때마다 출입권한을 다르게 설정할 수 있습니다."
                        }
                        touchIcon="QUESTION"
                        className="ml4"
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>
            {/* 방문자 초대 바로가기 섹션 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>방문자 초대 바로가기</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="">
                  <Link to={visitorUrl} target="_blank">
                    <BaseButton title="방문자 초대" className="color-white icon-invite" />
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </div>
      </form>
    </section>
  );
};

export default VisitorAccessSection;
